<template>
  <div class="fq-root" :class="{ 'fq-zoomed': zoomed }">
    <div class="fq-section" v-for="section in sections" :key="section.key">
      <div class="fq-section-title">{{ section.title }}</div>
      <div class="fq-hands">
        <div class="fq-hand">
          <div class="fq-hand-label">Left Hand</div>
          <svg viewBox="-10 0 220 240" class="fq-svg">
            <rect x="10" y="140" width="160" height="90" rx="40" class="fq-palm" />
            <g v-for="(f, i) in section.leftDigits" :key="'l' + i">
              <rect :x="f.x" :y="f.y" width="26" :height="f.height" rx="13" class="fq-finger-body" />
              <rect :x="f.x" :y="f.y" width="26" height="32" rx="13" class="fq-tip" :class="f.quality.cls">
                <title>{{ f.title }}: {{ f.quality.label }}{{ f.displayScore !== '—' ? ' (' + f.displayScore + ')' : '' }}</title>
              </rect>
              <text :x="f.x + 13" :y="f.y + 21" text-anchor="middle" class="fq-score">{{ f.displayScore }}</text>
              <text :x="f.x + 13" :y="228" text-anchor="middle" class="fq-finger-label">{{ f.label }}</text>
            </g>
          </svg>
        </div>
        <div class="fq-hand">
          <div class="fq-hand-label">Right Hand</div>
          <svg viewBox="-10 0 220 240" class="fq-svg">
            <rect x="10" y="140" width="160" height="90" rx="40" class="fq-palm" />
            <g v-for="(f, i) in section.rightDigits" :key="'r' + i">
              <rect :x="f.x" :y="f.y" width="26" :height="f.height" rx="13" class="fq-finger-body" />
              <rect :x="f.x" :y="f.y" width="26" height="32" rx="13" class="fq-tip" :class="f.quality.cls">
                <title>{{ f.title }}: {{ f.quality.label }}{{ f.displayScore !== '—' ? ' (' + f.displayScore + ')' : '' }}</title>
              </rect>
              <text :x="f.x + 13" :y="f.y + 21" text-anchor="middle" class="fq-score">{{ f.displayScore }}</text>
              <text :x="f.x + 13" :y="228" text-anchor="middle" class="fq-finger-label">{{ f.label }}</text>
            </g>
          </svg>
        </div>
      </div>
    </div>

    <div class="fq-legend">
      <span class="fq-legend-item"><i class="fq-dot fq-good"></i> Good (&gt;50)</span>
      <span class="fq-legend-item"><i class="fq-dot fq-fair"></i> Fair (30–50)</span>
      <span class="fq-legend-item"><i class="fq-dot fq-poor"></i> Poor (&lt;30)</span>
      <span class="fq-legend-item"><i class="fq-dot fq-none"></i> No Data</span>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    sections: { type: Array, default: () => [] },
    zoomed: { type: Boolean, default: false },
  },
};
</script>

<style lang="scss" scoped>
$font: 'Segoe UI', sans-serif;
$blue: #389ad4;

.fq-section {
  & + .fq-section {
    margin-top: 14px;
    padding-top: 14px;
    border-top: 1px dashed #e2e8f0;
  }
}

.fq-section-title {
  font-size: 9px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: $blue;
  background: #eef2fb;
  display: inline-block;
  padding: 2px 8px;
  border-radius: 20px;
  margin-bottom: 8px;
}

.fq-hands {
  display: flex;
  gap: 18px;
  justify-content: space-around;
}

.fq-hand {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

.fq-hand-label {
  font-size: 10.5px;
  font-weight: 700;
  color: #7a8499;
}

.fq-svg {
  width: 100px;
  height: 120px;
}

.fq-palm,
.fq-finger-body {
  fill: #eef2fb;
  stroke: #c7d2e0;
  stroke-width: 2;
}

.fq-tip {
  stroke-width: 1;
}

.fq-tip.fq-good { fill: #16a34a; stroke: #15803d; }
.fq-tip.fq-fair { fill: #d97706; stroke: #b45309; }
.fq-tip.fq-poor { fill: #e53e3e; stroke: #c53030; }
.fq-tip.fq-none { fill: #cbd5e1; stroke: #b0bac9; }

.fq-score {
  font-size: 11px;
  font-weight: 700;
  fill: #fff;
  font-family: $font;
  pointer-events: none;
}

.fq-finger-label {
  font-size: 9px;
  font-weight: 600;
  fill: #a0aab8;
  font-family: $font;
}

.fq-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid #f0f2f8;
}

.fq-legend-item {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 10.5px;
  color: #7a8499;
}

.fq-dot {
  width: 9px;
  height: 9px;
  border-radius: 50%;
  display: inline-block;

  &.fq-good { background: #16a34a; }
  &.fq-fair { background: #d97706; }
  &.fq-poor { background: #e53e3e; }
  &.fq-none { background: #cbd5e1; }
}

/* ── Zoomed sizing ── */
.fq-zoomed {
  .fq-hands { gap: 48px; }
  .fq-svg { width: 220px; height: 264px; }
  .fq-section-title { font-size: 12px; padding: 4px 12px; }
  .fq-hand-label { font-size: 13px; }
  .fq-score { font-size: 15px; }
  .fq-finger-label { font-size: 12px; }
  .fq-legend-item { font-size: 13px; }
  .fq-dot { width: 12px; height: 12px; }

  .fq-section + .fq-section {
    margin-top: 28px;
    padding-top: 28px;
  }
}
</style>
